import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, collection, query, where, getDocs, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import QuotationModal from './QuotationModal';
import QuotationFormBuilder from './QuotationFormBuilder';
import { FaCopy, FaEye, FaPlus, FaEllipsisV, FaUserPlus, FaHistory } from 'react-icons/fa';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

interface BaseQuotation {
  id: string;
  status: string;
  createdAt: Date | string;
}

interface AdminQuotation extends BaseQuotation {
  clientName: string;
  amount: number;
  status: 'active' | 'inactive' | 'pending' | 'completed' | 'cancelled';
  businessId: string;
  clientEmail?: string;
  customFields?: any[];
  createdBy?: string;
  createdByName?: string;
  assignedTo?: string;
  activityLog?: any[];
}

interface SubmittedQuotation {
  id: string;
  clientName: string;
  clientEmail: string;
  createdAt: Timestamp;
  status: string;
  '1750100522588'?: string;
  '1750100532828'?: string;
  [key: string]: any;
}

const Quotations: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { userData, currentUser } = useAuth();
  const [quotations, setQuotations] = useState<AdminQuotation[]>([]);
  const [editingQuotation, setEditingQuotation] = useState<any | null>(null);
  const [isFormBuilderOpen, setIsFormBuilderOpen] = useState(false);
  const [submittedQuotations, setSubmittedQuotations] = useState<SubmittedQuotation[]>([]);
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [selectedQuotationLog, setSelectedQuotationLog] = useState<any[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  // Elegir el locale de date-fns según el idioma
  const currentLocale = i18n.language === 'es' ? es : enUS;

  // Estados de cliente/modal eliminados temporalmente (no usados actualmente)

  // Estado para filtros
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const fetchQuotations = async () => {
      if (!userData?.businessId) return;

      try {
        const db = getFirestore();
        const quotationsRef = collection(db, 'quotations');
        const q = query(
          quotationsRef,
          where('businessId', '==', userData.businessId),
          where('deleted', '!=', true)
        );
        const querySnapshot = await getDocs(q);
        const quotationsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AdminQuotation[];
        setQuotations(quotationsData);
      } catch (error) {
        console.error('Error fetching quotations:', error);
        toast.error(t('common.error'));
      }
    };

    fetchQuotations();
  }, [userData?.businessId, t]);

  useEffect(() => {
    const fetchSubmittedQuotations = async () => {
      if (!userData?.businessId) return;

      try {
        const db = getFirestore();
        
        // Consultar las submissions del negocio
        const submissionsRef = collection(db, 'quotations', userData.businessId, 'submissions');
        const submissionsSnapshot = await getDocs(submissionsRef);
        
        const quotationsData = submissionsSnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Datos de la cotización:', data); // Debug log
          
          return {
            id: doc.id,
            clientName: data.clientName || 'Sin nombre',
            clientEmail: data.clientEmail || 'Sin email',
            createdAt: data.createdAt, // Mantener el Timestamp original
            status: data.status || 'unread',
            '1750100522588': data['1750100522588'] || '',
            '1750100532828': data['1750100532828'] || '',
            ...data
          } as SubmittedQuotation;
        });

        console.log('Cotizaciones procesadas:', quotationsData); // Debug log
        setSubmittedQuotations(quotationsData);
      } catch (err) {
        console.error('Error fetching submitted quotations:', err);
      }
    };

    fetchSubmittedQuotations();
  }, [userData?.businessId]);

  // (Cliente modal eliminado temporalmente)

  const handleSaveQuotation = async (
    quotationData: Omit<AdminQuotation, 'id' | 'createdAt' | 'businessId'> & { customFields?: any[] }
  ) => {
    if (!userData?.businessId) return;

    try {
      const db = getFirestore();
      const quotationsRef = collection(db, 'quotations');
      
      // Procesar campos personalizados para guardar { id, label, value }
      const customFields: any[] = Array.isArray(quotationData.customFields)
        ? quotationData.customFields.map((field: any) => ({
            id: field.id,
            label: field.label,
            value: field.value
          }))
        : [];
      
      const docRef = await addDoc(quotationsRef, {
        ...quotationData,
        customFields,
        createdAt: new Date().toISOString(),
        businessId: userData.businessId
      });

      setEditingQuotation(null);
      toast.success(t('common.success'));
      // Redirigir a la vista detallada de la cotización
      navigate(`/quotations/${docRef.id}`);
    } catch (error) {
      console.error('Error saving quotation:', error);
      toast.error(t('common.error'));
    }
  };



  const formLink = userData?.businessId 
    ? `${window.location.origin}/quotation-form/${userData.businessId}`
    : '';

  const handleViewQuotation = (quotationId: string) => {
    navigate(`/quotations/${quotationId}`);
    setOpenMenuId(null);
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cargar empleados al inicio para mostrar en la tabla
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!userData?.businessId) return;

      try {
        const db = getFirestore();
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef,
          where('businessId', '==', userData.businessId),
          where('role', '==', 'employee'),
          where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(q);
        const employeesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    fetchEmployees();
  }, [userData?.businessId]);

  const handleAssignQuotation = (quotationId: string) => {
    setSelectedQuotationId(quotationId);
    setIsAssignModalOpen(true);
    setOpenMenuId(null);
  };

  const handleConfirmAssign = async () => {
    console.log('handleConfirmAssign called', {
      selectedQuotationId,
      selectedEmployeeId,
      businessId: userData?.businessId,
      uid: currentUser?.uid
    });

    if (!selectedQuotationId || !selectedEmployeeId || !userData?.businessId || !currentUser?.uid) {
      console.log('Missing required data for assignment');
      toast.error(t('quotations.selectEmployee', 'Selecciona un empleado'));
      return;
    }

    try {
      const db = getFirestore();
      const quotationRef = doc(db, 'quotations', userData.businessId, 'submissions', selectedQuotationId);
      
      const assignedByName = `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || (userData as any).email || currentUser.email || 'Unknown';
      
      console.log('Updating quotation with:', {
        assignedTo: selectedEmployeeId,
        assignedBy: currentUser.uid,
        assignedByName
      });

      const now = new Date();
      const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
      const employeeName = selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : selectedEmployeeId;

      // Obtener el documento actual para preservar el log existente
      const quotationDoc = await getDoc(quotationRef);
      const currentLog = quotationDoc.exists() ? (quotationDoc.data().activityLog || []) : [];

      await updateDoc(quotationRef, {
        assignedTo: selectedEmployeeId,
        assignedAt: now,
        assignedBy: currentUser.uid,
        assignedByName: assignedByName,
        lastModifiedAt: now,
        lastModifiedBy: currentUser.uid,
        lastModifiedByName: assignedByName,
        activityLog: [
          ...currentLog,
          {
            action: 'assigned',
            timestamp: now,
            userId: currentUser.uid,
            userName: assignedByName,
            userRole: userData.role,
            details: `Assigned to ${employeeName}`,
            detailsKey: 'quotations.detailAssigned',
            employeeName: employeeName,
            assignedToId: selectedEmployeeId,
            assignedToName: employeeName
          }
        ]
      });

      console.log('Assignment successful');
      toast.success(t('quotations.assignedSuccess', 'Cotización asignada exitosamente'));
      setIsAssignModalOpen(false);
      setSelectedQuotationId(null);
      setSelectedEmployeeId('');
      
      // Recargar cotizaciones
      const submissionsRef = collection(db, 'quotations', userData.businessId, 'submissions');
      const submissionsSnapshot = await getDocs(submissionsRef);
      const quotationsData = submissionsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          clientName: data.clientName || 'Sin nombre',
          clientEmail: data.clientEmail || 'Sin email',
          createdAt: data.createdAt,
          status: data.status || 'unread',
          '1750100522588': data['1750100522588'] || '',
          '1750100532828': data['1750100532828'] || '',
          ...data
        } as SubmittedQuotation;
      });
      setSubmittedQuotations(quotationsData);
      console.log('Quotations reloaded');
    } catch (error) {
      console.error('Error assigning quotation:', error);
      toast.error(t('common.error'));
    }
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'unread':
        return 'bg-blue-100 text-blue-800';
      case 'read':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Lógica para crear un nuevo draft de cotización
  const handleCreateNewQuotation = async () => {
    if (!userData?.businessId || !currentUser?.uid) {
      toast.error(t('common.error'));
      return;
    }
    
    try {
      const db = getFirestore();
      const submissionsRef = collection(db, 'quotations', userData.businessId, 'submissions');
      
      const now = new Date();
      const userName = `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || (userData as any).email || currentUser.email;
      
      // Crear una nueva quotation en estado draft sin cliente
      const docRef = await addDoc(submissionsRef, {
        clientName: '',
        clientEmail: '',
        createdAt: now,
        status: 'draft',
        customFields: [],
        createdBy: currentUser.uid,
        createdByName: userName,
        createdByRole: userData.role,
        lastModifiedAt: now,
        lastModifiedBy: currentUser.uid,
        lastModifiedByName: userName,
        activityLog: [
          {
            action: 'created',
            timestamp: now,
            userId: currentUser.uid,
            userName: userName,
            userRole: userData.role,
            details: 'Quotation created as draft',
            detailsKey: 'quotations.detailCreated'
          }
        ]
      });

      // Navegar a la vista de la cotización recién creada
      navigate(`/quotations/${docRef.id}`);
      
      toast.success(t('quotations.draftCreated', 'Borrador creado. Selecciona un cliente para continuar.'));
    } catch (error) {
      console.error('Error creating draft quotation:', error);
      toast.error(t('common.error'));
    }
  };

  // Unificar cotizaciones de ambas fuentes y normalizar propiedades
  const allQuotations = [
    ...quotations.map(q => ({
      id: q.id,
      clientName: q.clientName || '',
      clientEmail: q.clientEmail || '',
      amount: q.amount ?? '',
      createdAt: q.createdAt,
      status: q.status,
      customFields: q.customFields || [],
      businessId: q.businessId || userData?.businessId || '',
      source: 'admin',
      createdBy: q.createdBy,
      createdByName: q.createdByName,
      assignedTo: q.assignedTo,
      activityLog: q.activityLog || []
    })),
    ...submittedQuotations.map(q => ({
      id: q.id,
      clientName: q.clientName || '',
      clientEmail: q.clientEmail || '',
      amount: q.amount ?? '',
      createdAt: q.createdAt,
      status: q.status,
      customFields: q.customFields || [],
      businessId: userData?.businessId || '',
      source: 'submission',
      createdBy: q.createdBy,
      createdByName: q.createdByName,
      assignedTo: q.assignedTo,
      activityLog: q.activityLog || []
    }))
  ]
  .filter(quotation => {
    // Para employees, solo mostrar cotizaciones creadas por ellos o asignadas a ellos
    if ((userData as any)?.role === 'employee') {
      const createdByEmployee = quotation.createdBy === currentUser?.uid;
      const assignedToEmployee = quotation.assignedTo === currentUser?.uid;
      if (!createdByEmployee && !assignedToEmployee) {
        return false;
      }
    }
    
    // Aplicar filtro por estado
    if (statusFilter === 'all') return true;
    return quotation.status === statusFilter;
  })
  .sort((a, b) => {
    // Ordenar por fecha de creación en orden descendente (más reciente primero)
    const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() : new Date(a.createdAt);
    const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() : new Date(b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });



  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header mejorado */}
        <div className="bg-white rounded-3xl shadow-xl border border-white/40 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent mb-2">
                {t('quotations.title')}
              </h1>
              <p className="text-gray-600">
                {allQuotations.length} {allQuotations.length === 1 ? t('quotations.quotationRegistered', 'cotización registrada') : t('quotations.quotationsRegistered', 'cotizaciones registradas')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {(userData as any)?.role !== 'employee' && (
                <button 
                  onClick={() => setIsFormBuilderOpen(true)}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {t('quotations.formBuilder.title')}
                </button>
              )}
              <button 
                onClick={handleCreateNewQuotation}
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <FaPlus className="text-lg" /> {t('quotations.addQuotation')}
              </button>
            </div>
          </div>
        </div>

        {(userData as any)?.role !== 'employee' && (
          <div className="mb-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('quotations.formBuilder.formLink')}</h3>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <input 
                  type="text" 
                  value={formLink} 
                  readOnly 
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-gray-50 cursor-text focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(formLink);
                    toast.success(t('quotations.formBuilder.copied'));
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold shadow hover:shadow-md transition-all"
                >
                  <FaCopy /> {t('quotations.formBuilder.copy')}
                </button>
              </div>
              <button
                onClick={() => {
                  const embedCode = `<iframe src=\"${formLink}\" width=\"100%\" height=\"600\" style=\"border:none;\"></iframe>`;
                  navigator.clipboard.writeText(embedCode);
                  toast.success(t('quotations.formBuilder.copied'));
                }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow hover:shadow-md transition-all"
              >
                <FaCopy /> {t('quotations.formBuilder.copyEmbed')}
              </button>
            </div>
          </div>
        )}

        {/* Status Filters */}
        <div className="mb-6 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <span className="font-semibold text-gray-900 text-lg">{t('quotations.filterByStatus', 'Filtrar por estado')}:</span>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 border-2 ${
                  statusFilter === 'all'
                    ? 'bg-red-600 text-white border-red-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-red-400 hover:text-red-600'
                }`}
              >
                All ({quotations.length + submittedQuotations.length})
              </button>
              <button
                onClick={() => setStatusFilter('unread')}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 border-2 ${
                  statusFilter === 'unread'
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                }`}
              >
                Unread ({allQuotations.filter(q => q.status === 'unread').length})
              </button>
              <button
                onClick={() => setStatusFilter('read')}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 border-2 ${
                  statusFilter === 'read'
                    ? 'bg-green-600 text-white border-green-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-600'
                }`}
              >
                Read ({allQuotations.filter(q => q.status === 'read').length})
              </button>
              <button
                onClick={() => setStatusFilter('sent')}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 border-2 ${
                  statusFilter === 'sent'
                    ? 'bg-orange-600 text-white border-orange-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:text-orange-600'
                }`}
              >
                Sent ({allQuotations.filter(q => q.status === 'sent').length})
              </button>
              <button
                onClick={() => setStatusFilter('converted')}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 border-2 ${
                  statusFilter === 'converted'
                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg'
                    : 'bg-purple-200 text-gray-700 border-purple-300 hover:border-purple-400 hover:text-purple-600'
                }`}
              >
                Converted ({allQuotations.filter(q => q.status === 'converted').length})
              </button>
            </div>
          </div>
        </div>
        {/* Results Counter */}
        <div className="mb-4 text-right">
          <span className="text-gray-600 font-medium">
            {t('quotations.showing', 'Mostrando')} {allQuotations.length} {allQuotations.length !== 1 ? t('quotations.quotations', 'cotizaciones') : t('quotations.quotation', 'cotización')}
            {statusFilter !== 'all' && ` ${t('quotations.withStatus', 'con estado')} "${statusFilter}"`}
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-gradient-to-r from-red-50 to-pink-50">
                <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                  {t('quotations.client', 'Cliente')}
                </th>
                <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                  {t('quotations.createdBy', 'Creado por')}
                </th>
                <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                  {t('quotations.createdAt', 'Fecha de creación')}
                </th>
                <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                  {t('quotations.status', 'Estado')}
                </th>
                {(userData as any)?.role !== 'employee' && (
                  <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                    {t('quotations.assignedTo', 'Asignado a')}
                  </th>
                )}
                <th className="p-4 text-gray-900 font-bold border-b-2 border-gray-200">
                  {t('quotations.actions', 'Acciones')}
                </th>
              </tr>
            </thead>
            <tbody>
              {allQuotations.length === 0 ? (
                <tr>
                  <td colSpan={(userData as any)?.role === 'employee' ? 5 : 6} className="p-16">
                    <div className="text-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
                        <FaPlus className="text-4xl text-red-400" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{t('quotations.noQuotations')}</h3>
                      <p className="text-gray-500 mb-6">{t('quotations.startCreatingMessage', 'Comienza creando tu primera cotización')}</p>
                      <button 
                        onClick={handleCreateNewQuotation}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                      >
                        <FaPlus />
                        {t('quotations.createFirst', 'Crear primera cotización')}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                allQuotations.map((quotation) => (
                  <tr key={quotation.id} className="hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 border-b border-gray-100 last:border-b-0">
                    <td className="p-4">
                      <div>
                        <div className="font-semibold text-gray-900">{quotation.clientName || t('quotations.noClient', 'Sin cliente')}</div>
                        {quotation.clientEmail && <div className="text-gray-500 text-sm">{quotation.clientEmail}</div>}
                      </div>
                    </td>

                    <td className="p-4 text-gray-700">
                      <div className="text-sm">
                        {quotation.createdByName || quotation.createdBy || t('quotations.system', 'Sistema')}
                      </div>
                    </td>

                    <td className="p-4 text-gray-700">
                      {quotation.createdAt instanceof Timestamp
                        ? format(quotation.createdAt.toDate(), i18n.language === 'es' ? "d 'de' MMMM 'de' yyyy" : "MMMM d, yyyy", { locale: currentLocale })
                        : quotation.createdAt
                          ? format(new Date(quotation.createdAt), i18n.language === 'es' ? "d 'de' MMMM 'de' yyyy" : "MMMM d, yyyy", { locale: currentLocale })
                          : ''}
                    </td>
                    <td className="p-4">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusBadgeClasses(quotation.status)}`}>
                        {quotation.status}
                      </span>
                    </td>
                    {(userData as any)?.role !== 'employee' && (
                      <td className="p-4 text-gray-700">
                        <div className="text-sm">
                          {quotation.assignedTo ? (
                            <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-1 rounded-full text-xs font-medium">
                              <FaUserPlus className="text-xs" />
                              {employees.find(e => e.id === quotation.assignedTo)?.firstName} {employees.find(e => e.id === quotation.assignedTo)?.lastName || quotation.assignedTo}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">{t('quotations.notAssigned', 'Sin asignar')}</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="p-4 relative">
                      <button 
                        data-quotation-id={quotation.id}
                        onClick={() => setOpenMenuId(openMenuId === quotation.id ? null : quotation.id)}
                        className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all"
                      >
                        <FaEllipsisV />
                      </button>
                      
                      {openMenuId === quotation.id && (
                        <div 
                          ref={menuRef}
                          className="fixed w-48 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999]"
                          style={{
                            bottom: `${window.innerHeight - (document.querySelector(`[data-quotation-id="${quotation.id}"]`) as HTMLElement)?.getBoundingClientRect().top + 8}px`,
                            right: `${window.innerWidth - (document.querySelector(`[data-quotation-id="${quotation.id}"]`) as HTMLElement)?.getBoundingClientRect().right}px`
                          }}
                        >
                          <div className="py-2">
                            <button
                              onClick={() => {
                                handleViewQuotation(quotation.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                            >
                              <FaEye className="text-blue-500" />
                              {t('quotations.view', 'Ver')}
                            </button>
                            {userData?.role === 'business' && (
                              <>
                                <button
                                  onClick={() => {
                                    handleAssignQuotation(quotation.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                >
                                  <FaUserPlus className="text-green-500" />
                                  {t('quotations.assign', 'Asignar')}
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedQuotationLog(quotation.activityLog || []);
                                    setIsLogModalOpen(true);
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                                >
                                  <FaHistory className="text-purple-500" />
                                  {t('quotations.viewLog', 'Ver Log')}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {editingQuotation && (
          <QuotationModal
            isOpen={!!editingQuotation}
            quotation={editingQuotation}
            onClose={() => setEditingQuotation(null)}
            onSubmit={handleSaveQuotation}
            businessId={userData?.businessId || ''}
          />
        )}

        {isFormBuilderOpen && (
          <QuotationFormBuilder
            isOpen={isFormBuilderOpen}
            onClose={() => setIsFormBuilderOpen(false)}
          />
        )}

        {/* Modal de asignación */}
        {isAssignModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Header del modal */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-50 to-pink-50">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FaUserPlus className="text-red-500" />
                  {t('quotations.assignQuotation', 'Asignar Cotización')}
                </h2>
                <button
                  onClick={() => {
                    setIsAssignModalOpen(false);
                    setSelectedQuotationId(null);
                    setSelectedEmployeeId('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="p-6">
                <p className="text-gray-600 mb-4">
                  {t('quotations.selectEmployeeToAssign', 'Selecciona un empleado para asignar esta cotización:')}
                </p>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('quotations.employee', 'Empleado')}
                  </label>
                  <select
                    value={selectedEmployeeId}
                    onChange={(e) => setSelectedEmployeeId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white text-gray-900 text-base transition-all duration-300 focus:outline-none focus:border-red-600"
                  >
                    <option value="">{t('quotations.selectEmployee', 'Selecciona un empleado')}</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.firstName} {employee.lastName} - {employee.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Botones */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsAssignModalOpen(false);
                      setSelectedQuotationId(null);
                      setSelectedEmployeeId('');
                    }}
                    className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all"
                  >
                    {t('common.cancel', 'Cancelar')}
                  </button>
                  <button
                    onClick={handleConfirmAssign}
                    disabled={!selectedEmployeeId}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:from-gray-300 disabled:to-gray-300 text-white rounded-xl font-semibold transition-all disabled:cursor-not-allowed"
                  >
                    {t('quotations.assign', 'Asignar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Log de Actividad */}
        {isLogModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[80vh] flex flex-col">
              {/* Header del modal */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <FaHistory className="text-purple-500" />
                  {t('quotations.activityLog', 'Registro de Actividad')}
                </h2>
                <button
                  onClick={() => {
                    setIsLogModalOpen(false);
                    setSelectedQuotationLog([]);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="p-6 overflow-y-auto flex-1">
                {selectedQuotationLog.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FaHistory className="mx-auto text-4xl mb-3 opacity-30" />
                    <p>{t('quotations.noActivityYet', 'Sin actividad registrada')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedQuotationLog
                      .sort((a, b) => {
                        const timeA = a.timestamp?.toDate?.() || new Date(a.timestamp);
                        const timeB = b.timestamp?.toDate?.() || new Date(b.timestamp);
                        return timeB.getTime() - timeA.getTime();
                      })
                      .map((log, index) => {
                        const logDate = log.timestamp?.toDate?.() || new Date(log.timestamp);
                        const actionColors = {
                          created: 'bg-blue-100 text-blue-700 border-blue-300',
                          assigned: 'bg-green-100 text-green-700 border-green-300',
                          read: 'bg-yellow-100 text-yellow-700 border-yellow-300',
                          sent: 'bg-orange-100 text-orange-700 border-orange-300',
                          converted: 'bg-purple-100 text-purple-700 border-purple-300'
                        };
                        const actionLabels = {
                          created: t('quotations.actionCreated', 'Creada'),
                          assigned: t('quotations.actionAssigned', 'Asignada'),
                          read: t('quotations.actionRead', 'Leída'),
                          sent: t('quotations.actionSent', 'Enviada'),
                          converted: t('quotations.actionConverted', 'Convertida')
                        };

                        return (
                          <div
                            key={index}
                            className={`border-l-4 pl-4 py-3 ${actionColors[log.action as keyof typeof actionColors] || 'bg-gray-100 text-gray-700 border-gray-300'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-sm mb-1">
                                  {actionLabels[log.action as keyof typeof actionLabels] || log.action}
                                </div>
                                <div className="text-xs opacity-75 mb-1">
                                  {log.userName} ({log.userRole})
                                </div>
                                <div className="text-xs">
                                  {log.detailsKey 
                                    ? log.employeeName 
                                      ? t(log.detailsKey, { employeeName: log.employeeName })
                                      : t(log.detailsKey)
                                    : log.details}
                                </div>
                              </div>
                              <div className="text-xs opacity-60 whitespace-nowrap ml-4">
                                {format(logDate, 'PPp', { locale: currentLocale })}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Footer del modal */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    setIsLogModalOpen(false);
                    setSelectedQuotationLog([]);
                  }}
                  className="w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-all"
                >
                  {t('common.close', 'Cerrar')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de selección de cliente */}
      </div>
    </div>
  );
};

export default Quotations; 